import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Edit3, Download, Clock, Calendar as CalendarIcon,
  Save, Plus, Trash2, RefreshCw, Settings2, X, GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../utils/admin-api';
import { generateCustomPLCReport } from '../../utils/advanced-pdf-generator';

interface ManualLoggingProps {
  clientId: string;
  token: string;
  permissions: any;
}

// ── Custom field definition ────────────────────────────────────────────────────
interface CustomField {
  id: string;
  label: string;
  type: 'text' | 'number';
  placeholder?: string;
}

const CUSTOM_FIELDS_KEY = (clientId: string) => `manual_log_custom_fields_${clientId}`;

const defaultForm = () => ({
  machine: '',
  startTime: { hour: '08', minute: '00', period: 'AM' as 'AM' | 'PM' },
  endTime: { hour: '05', minute: '00', period: 'PM' as 'AM' | 'PM' },
  passCount: 0,
  rejectCount: 0,
  operator: '',
  notes: '',
  customValues: {} as Record<string, string>,
});

export function ManualLogging({ clientId, token, permissions }: ManualLoggingProps) {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [formData, setFormData] = useState(defaultForm());
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);

  // Custom columns stored per client in localStorage
  const [customFields, setCustomFields] = useState<CustomField[]>(() => {
    try {
      const stored = localStorage.getItem(CUSTOM_FIELDS_KEY(clientId));
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<'text' | 'number'>('text');

  useEffect(() => { loadEntries(); }, [startDate, endDate]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const options: any = { limit: 500 };
      if (startDate) options.startDate = format(startDate, 'yyyy-MM-dd');
      if (endDate) options.endDate = format(endDate, 'yyyy-MM-dd');
      const response = await adminApi.getManualLogs(token, clientId, options);
      setEntries(response.data || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Custom Fields management ───────────────────────────────────────────────
  const saveCustomFields = (fields: CustomField[]) => {
    setCustomFields(fields);
    localStorage.setItem(CUSTOM_FIELDS_KEY(clientId), JSON.stringify(fields));
  };

  const addCustomField = () => {
    const label = newFieldLabel.trim();
    if (!label) { toast.error('Column name is required'); return; }
    if (customFields.some(f => f.label.toLowerCase() === label.toLowerCase())) {
      toast.error('Column already exists'); return;
    }
    const newField: CustomField = { id: `cf_${Date.now()}`, label, type: newFieldType, placeholder: `Enter ${label}` };
    saveCustomFields([...customFields, newField]);
    setNewFieldLabel('');
    toast.success(`Column "${label}" added`);
  };

  const removeCustomField = (id: string) => {
    saveCustomFields(customFields.filter(f => f.id !== id));
    toast.success('Column removed');
  };

  // ── Time helpers ───────────────────────────────────────────────────────────
  const timeStr = (t: typeof formData.startTime) =>
    `${t.hour}:${t.minute} ${t.period}`;

  const handleTimeChange = (type: 'start' | 'end', field: 'hour' | 'minute', value: string) => {
    const key = type === 'start' ? 'startTime' : 'endTime';
    setFormData(prev => ({ ...prev, [key]: { ...prev[key], [field]: value.padStart(2, '0') } }));
  };

  const togglePeriod = (type: 'start' | 'end') => {
    const key = type === 'start' ? 'startTime' : 'endTime';
    setFormData(prev => ({ ...prev, [key]: { ...prev[key], period: prev[key].period === 'AM' ? 'PM' : 'AM' } }));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!permissions?.manualLogging?.edit) { toast.error('No permission to add logs'); return; }
    if (!formData.machine) { toast.error('Machine name is required'); return; }

    try {
      setSaving(true);

      // Build notes combining regular notes + custom field values
      const customParts = customFields
        .filter(f => formData.customValues[f.id]?.trim())
        .map(f => `${f.label}: ${formData.customValues[f.id]}`);

      const notesWithCustom = [
        ...(formData.notes ? [formData.notes] : []),
        ...customParts,
      ].join('\n');

      // Build a json blob for custom fields to preserve structure
      const customFieldsData = customFields.reduce((acc, f) => {
        if (formData.customValues[f.id] !== undefined) {
          acc[f.label] = formData.customValues[f.id];
        }
        return acc;
      }, {} as Record<string, string>);

      await adminApi.createManualLog(token, clientId, {
        logDate: format(new Date(), 'yyyy-MM-dd'),
        machine: formData.machine,
        startTime: timeStr(formData.startTime),
        endTime: timeStr(formData.endTime),
        passCount: formData.passCount,
        rejectCount: formData.rejectCount,
        operator: formData.operator || null,
        notes: Object.keys(customFieldsData).length > 0
          ? `${formData.notes || ''}\n__CUSTOM__${JSON.stringify(customFieldsData)}`
          : (formData.notes || null),
      });

      await adminApi.logClientAuditEvent(token, clientId, {
        eventType: 'manual_log_created',
        description: 'Created a manual log entry',
        metadata: {
          machine: formData.machine,
          operator: formData.operator || null,
          passCount: formData.passCount,
          rejectCount: formData.rejectCount,
        },
      }).catch((error) => {
        console.warn('Failed to record manual log creation audit event:', error);
      });

      toast.success('Production entry saved successfully');
      setFormData(defaultForm());
      loadEntries();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    if (!permissions?.manualLogging?.edit) { toast.error('No permission to delete'); return; }
    if (!confirm('Delete this entry?')) return;
    try {
      await adminApi.deleteManualLog(token, clientId, String(id));
      await adminApi.logClientAuditEvent(token, clientId, {
        eventType: 'manual_log_deleted',
        description: 'Deleted a manual log entry',
        metadata: {
          logId: String(id),
        },
      }).catch((error) => {
        console.warn('Failed to record manual log delete audit event:', error);
      });
      toast.success('Entry deleted');
      loadEntries();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async (fmt: 'csv' | 'json') => {
    if (!permissions?.manualLogging?.download) { toast.error('No permission to download'); return; }
    try {
      const options: any = {};
      if (startDate) options.startDate = format(startDate, 'yyyy-MM-dd');
      if (endDate) options.endDate = format(endDate, 'yyyy-MM-dd');
      const blob = await adminApi.downloadManualLogs(token, clientId, fmt, options);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `manual_logs_${date}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch {
      // Fallback to client-side CSV from loaded entries
      const customLabels = customFields.map(f => f.label);
      const headers = ['Date', 'Machine', 'Start Time', 'End Time', 'Pass', 'Reject', 'Total', 'Operator', 'Notes', ...customLabels];
      const rows = entries.map(e => {
        let customData: Record<string, string> = {};
        const notes = e.notes || e.note || '';
        if (notes.includes('__CUSTOM__')) {
          try { customData = JSON.parse(notes.split('__CUSTOM__')[1]); } catch { }
        }
        return [
          e.log_date || e.logDate || '',
          e.machine || '',
          e.start_time || e.startTime || '',
          e.end_time || e.endTime || '',
          e.pass_count ?? e.passCount ?? 0,
          e.reject_count ?? e.rejectCount ?? 0,
          (e.pass_count ?? e.passCount ?? 0) + (e.reject_count ?? e.rejectCount ?? 0),
          e.operator || '',
          notes.split('__CUSTOM__')[0].trim(),
          ...customLabels.map(l => customData[l] || ''),
        ];
      });
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `manual_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exported from loaded data');
    }
  };

    const handleExportPDF = async () => {
      if (!permissions?.manualLogging?.download) { toast.error('No download permission'); return; }
      try {
        const token_str = sessionStorage.getItem('authToken') || token;
        const res = await adminApi.getPDFTemplate(token_str, clientId);
        let template = res?.advancedTemplate;
      
        if (!template) {
          template = {
            reportTitle: 'Manual Logging Report',
            reportSubtitle: 'Manual Logging Records for {clientName}',
            sections: [
              {
                id: 'logs',
                title: 'Logging Records',
                columns: [
                  { dataKey: 'log_date', label: 'Date' },
                  { dataKey: 'machine', label: 'Machine' },
                  { dataKey: 'start_time', label: 'Start Time' },
                  { dataKey: 'end_time', label: 'End Time' },
                  { dataKey: 'pass_count', label: 'Pass' },
                  { dataKey: 'reject_count', label: 'Reject' },
                  { dataKey: 'operator', label: 'Operator' }
                ]
              }
            ],
            primaryColor: '#a855f7',
            borderStyle: 'solid',
            fontSize: 10
          };
        }

        const historicalData = entries.map(e => ({
          log_date: e.log_date || e.logDate || '',
          machine: e.machine || '',
          start_time: e.start_time || e.startTime || '',
          end_time: e.end_time || e.endTime || '',
          pass_count: e.pass_count ?? e.passCount ?? 0,
          reject_count: e.reject_count ?? e.rejectCount ?? 0,
          operator: e.operator || ''
        }));

        // Use correct call signature: (liveData, historicalData, template, clientName, startDate?, endDate?)
        await generateCustomPLCReport([], historicalData, template, 'Manual Logging Client', startDate, endDate);
        await adminApi.logClientAuditEvent(token_str, clientId, {
          eventType: 'pdf_download',
          description: 'Downloaded manual logging PDF report',
          metadata: {
            report: 'manual_logging',
            fileName: `manual_logs_${new Date().toISOString().split('T')[0]}.pdf`,
            recordCount: historicalData.length,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
          },
        }).catch((error) => {
          console.warn('Failed to record manual logging PDF audit event:', error);
        });
        toast.success('PDF exported successfully');
      } catch (e: any) {
        console.error('PDF export error:', e);
        toast.error(e.message || 'PDF export failed');
      }
    };

  // ── Parse custom data from a log entry ────────────────────────────────────
  const parseCustomData = (entry: any): Record<string, string> => {
    const notes = entry.notes || entry.note || '';
    if (notes.includes('__CUSTOM__')) {
      try { return JSON.parse(notes.split('__CUSTOM__')[1]); } catch { }
    }
    return {};
  };

  const cleanNotes = (entry: any) => {
    const notes = entry.notes || entry.note || '';
    return notes.includes('__CUSTOM__') ? notes.split('__CUSTOM__')[0].trim() : notes;
  };

  const totalProduction = formData.passCount + formData.rejectCount;

  if (!permissions?.manualLogging?.view) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">You do not have permission to view this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center shadow-lg">
            <Edit3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Manual Logging</h2>
            <p className="text-xs text-muted-foreground">Record production data with custom fields</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Date filters */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {startDate ? format(startDate, 'MMM dd') : 'From'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={setStartDate} /></PopoverContent>
          </Popover>
          <span className="text-muted-foreground text-sm">to</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {endDate ? format(endDate, 'MMM dd') : 'To'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={setEndDate} /></PopoverContent>
          </Popover>
          <Button variant="ghost" size="sm" onClick={loadEntries}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {/* Column manager */}
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowColumnManager(true)}>
            <Settings2 className="h-4 w-4" />Columns ({customFields.length})
          </Button>
          {permissions?.manualLogging?.download && (
            <>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('csv')}>
                <Download className="h-4 w-4" />CSV
              </Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => handleExport('json')}>
                <Download className="h-4 w-4" />JSON
              </Button>
              <Button size="sm" onClick={handleExportPDF} className="gap-2 h-auto font-semibold bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 shadow-md shadow-purple-500/20 text-white">
                <Download className="h-3.5 w-3.5" />Export PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Column Manager Modal */}
      <AnimatePresence>
        {showColumnManager && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowColumnManager(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background border rounded-xl shadow-2xl w-full max-w-md p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold">Manage Custom Columns</h3>
                  <p className="text-xs text-muted-foreground">Add extra fields to your log entries</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowColumnManager(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Add new field */}
              <div className="space-y-3 mb-4 p-3 bg-muted/30 rounded-lg border">
                <p className="text-sm font-medium">Add New Column</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Column name (e.g. Shift, Product)"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomField()}
                    className="flex-1"
                  />
                  <select
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value as 'text' | 'number')}
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                  </select>
                </div>
                <Button onClick={addCustomField} size="sm" className="w-full gap-2">
                  <Plus className="h-4 w-4" />Add Column
                </Button>
              </div>

              {/* Existing fields */}
              {customFields.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No custom columns yet. Add one above.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Columns</p>
                  {customFields.map((field) => (
                    <div key={field.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg border">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm font-medium">{field.label}</span>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">{field.type}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeCustomField(field.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Entry Form */}
      <Card className="border-border/60">
        <CardHeader className="pb-4 border-b border-border/40">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center">
              <Save className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">New Production Entry</CardTitle>
              <CardDescription className="text-xs mt-0">
                {format(new Date(), 'MMMM do, yyyy')}{customFields.length > 0 && ` · ${customFields.length} custom column${customFields.length > 1 ? 's' : ''}`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Date + Times */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date (Auto)</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center text-sm">
                {format(new Date(), 'MMM do, yyyy')}
              </div>
            </div>
            {(['start', 'end'] as const).map((type) => {
              const t = type === 'start' ? formData.startTime : formData.endTime;
              return (
                <div key={type} className="space-y-2">
                  <Label>{type === 'start' ? 'Start' : 'End'} Time</Label>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input type="number" min="1" max="12" value={t.hour}
                      onChange={(e) => handleTimeChange(type, 'hour', e.target.value)}
                      className="w-14 text-center px-1" />
                    <span>:</span>
                    <Input type="number" min="0" max="59" value={t.minute}
                      onChange={(e) => handleTimeChange(type, 'minute', e.target.value)}
                      className="w-14 text-center px-1" />
                    <Button type="button" size="sm" variant={t.period === 'AM' ? 'default' : 'outline'}
                      className="px-2 text-xs" onClick={() => togglePeriod(type)}>
                      {t.period}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Machine + Counts */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="machine">Machine / Production Line *</Label>
              <Input id="machine" placeholder="Enter machine name" value={formData.machine}
                onChange={(e) => setFormData({ ...formData, machine: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pass">Pass Count</Label>
              <Input id="pass" type="number" min="0" value={formData.passCount}
                onChange={(e) => setFormData({ ...formData, passCount: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reject">Reject Count</Label>
              <Input id="reject" type="number" min="0" value={formData.rejectCount}
                onChange={(e) => setFormData({ ...formData, rejectCount: parseInt(e.target.value) || 0 })} />
            </div>
          </div>

          {/* Total Production Display */}
          {(formData.passCount > 0 || formData.rejectCount > 0) && (
            <div className="grid grid-cols-3 gap-3 p-3 bg-muted/40 rounded-lg border">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Pass</p>
                <p className="font-bold text-green-600">{formData.passCount}</p>
              </div>
              <div className="text-center border-x">
                <p className="text-xs text-muted-foreground">Reject</p>
                <p className="font-bold text-red-600">{formData.rejectCount}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="font-bold">{totalProduction}</p>
              </div>
            </div>
          )}

          {/* Operator + Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="operator">Operator (Optional)</Label>
              <Input id="operator" placeholder="Operator name" value={formData.operator}
                onChange={(e) => setFormData({ ...formData, operator: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input id="notes" placeholder="Additional notes" value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
            </div>
          </div>

          {/* Dynamic Custom Fields */}
          {customFields.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3 flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-muted-foreground" />
                Custom Fields
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-1">
                    <Label htmlFor={`cf-${field.id}`}>{field.label}</Label>
                    <Input
                      id={`cf-${field.id}`}
                      type={field.type}
                      placeholder={field.placeholder || `Enter ${field.label}`}
                      value={formData.customValues[field.id] || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        customValues: { ...prev.customValues, [field.id]: e.target.value }
                      }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button onClick={handleSubmit} disabled={saving}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 gap-2">
            {saving ? <><span className="animate-spin">⏳</span> Saving...</> : <><Save className="h-4 w-4" />Save Entry</>}
          </Button>
        </CardContent>
      </Card>

      {/* Historical Entries Table */}
      <Card className="border-border/60">
        <CardHeader className="pb-4 border-b border-border/40">
          <CardTitle className="text-base font-semibold flex items-center justify-between">
            <span>Production Log {startDate && endDate ? `· ${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd')}` : '· All Records'}</span>
            <span className="text-sm font-normal px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{entries.length} entries</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Edit3 className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No entries yet</p>
              <p className="text-xs mt-1">Add your first production entry above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground text-[11px] uppercase tracking-widest">
                    {['Date', 'Machine', 'Time', 'Pass', 'Reject', 'Total', 'Operator'].map(h => (
                      <th key={h} className="py-3 px-3 sm:px-4 font-semibold">{h}</th>
                    ))}
                    {customFields.map(f => (
                      <th key={f.id} className="py-3 px-3 sm:px-4 font-semibold">{f.label}</th>
                    ))}
                    <th className="py-3 px-3 sm:px-4 font-semibold">Notes</th>
                    {permissions?.manualLogging?.edit && <th className="py-3 px-3 sm:px-4 text-right font-semibold">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => {
                    const customData = parseCustomData(entry);
                    const notes = cleanNotes(entry);
                    const pass = entry.pass_count ?? entry.passCount ?? 0;
                    const reject = entry.reject_count ?? entry.rejectCount ?? 0;
                    return (
                      <motion.tr
                        key={entry.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors group"
                      >
                        <td className="py-3 px-3 sm:px-4 font-mono text-xs text-muted-foreground">{entry.log_date || entry.logDate || '—'}</td>
                        <td className="py-3 px-3 sm:px-4 font-semibold">{entry.machine || '—'}</td>
                        <td className="py-3 px-3 sm:px-4 text-xs text-muted-foreground whitespace-nowrap">
                          {entry.start_time || entry.startTime || '—'} – {entry.end_time || entry.endTime || '—'}
                        </td>
                        <td className="py-3 px-3 sm:px-4">
                          <span className="font-bold text-emerald-500 tabular-nums">{pass}</span>
                        </td>
                        <td className="py-3 px-3 sm:px-4">
                          <span className="font-bold text-red-500 tabular-nums">{reject}</span>
                        </td>
                        <td className="py-3 px-3 sm:px-4">
                          <span className="font-bold tabular-nums">{pass + reject}</span>
                        </td>
                        <td className="py-3 px-3 sm:px-4 text-muted-foreground">{entry.operator || '—'}</td>
                        {customFields.map(f => (
                          <td key={f.id} className="py-3 px-3 sm:px-4 text-muted-foreground text-xs">
                            {customData[f.label] || '—'}
                          </td>
                        ))}
                        <td className="py-3 px-3 sm:px-4 text-muted-foreground max-w-[160px] truncate" title={notes}>{notes || '—'}</td>
                        {permissions?.manualLogging?.edit && (
                          <td className="py-3 px-3 sm:px-4 text-right">
                            <Button size="icon" variant="ghost"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/10"
                              onClick={() => handleDelete(entry.id)}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
